import {
  rdbV1FiltersTeamsList,
  rdbV1FiltersYearsList,
  rdbV2OrganizationsTransfersDetail,
} from '@on3/api';
import type { ICommitsIndexProps } from '@on3/ui-lib/src/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { TransfersList } from '@public/components/College/Interests/Transfers/TransfersList';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { createDescription, createTitle } from '@public/utils/meta';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import type { FC } from 'react';

const TransfersIndex: FC<ICommitsIndexProps> = (props) => {
  const { currentSite } = props?.siteData || {};
  const router = useRouter();
  const { year, sport } = router.query;
  const orgName =
    currentSite?.organization?.knownAs ?? currentSite?.organization?.name;
  const orgMascot = currentSite?.organization?.mascot;
  const title = createTitle(year, orgName, orgMascot, sport, 'transfers');
  const description = createDescription(
    '{0} {1} {2} {3} team transfers list.',
    year,
    orgName,
    orgMascot,
    sport,
  );
  const noIndex = (props?.playerList?.pagination?.count || 0) === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <TransfersList
        {...props}
        key={`${year}-${sport}-${orgName}`}
        title={title}
        toggled={false}
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps<
  ICommitsIndexProps | []
> = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  const orgKey = siteData?.currentSite?.organizationKey;
  const { year, sport } = ctx.query as { year: string; sport: string };

  const { rankingSports } = siteData || {};
  const sportKey = rankingSports?.find(
    (sp) => sp?.name && slugify(sp?.name) === sport,
  )?.key;

  if (!orgKey || !year || !sport || !sportKey) {
    return {
      notFound: true,
    };
  }

  try {
    const params = {
      orgKey,
      sportKey,
      year: +year,
    };
    const teamFilterParams = {
      sportKey,
      year: params.year,
      groupBy: true,
    };
    const [playerList, teams, years] = await Promise.allSettled([
      rdbV2OrganizationsTransfersDetail(proxyApi, params),
      rdbV1FiltersTeamsList(proxyApi, teamFilterParams),
      rdbV1FiltersYearsList(proxyApi),
    ]);

    if (playerList.status === 'rejected') {
      return {
        notFound: true,
      };
    }

    const data = {
      pageType: 'database',
      siteData,
      userData,
      playerList: getFulfilledValue(playerList),
      teams: getFulfilledValue(teams),
      years: getFulfilledValue(years),
    } as ICommitsIndexProps;

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(TransfersIndex);
